import FamlaServices from './FamlaServices';
import { config } from '../config';

const base = () => config.api.baseUrl.replace(/\/$/, '');

export async function createCollectDataCollection(
    payload: { name: string; industry?: string; function?: string; locale?: 'fr' | 'en' },
    accessToken: string
) {
    return FamlaServices('api_host', '/collections', 'POST', payload, accessToken);
}

export async function getCollectDataCollection(collectionId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}`, 'GET', '', accessToken);
}

export type RecentCollectCollectionSummary = {
    id: string;
    name: string;
    company_id?: string | null;
    status: string;
    created_at?: string;
    updated_at?: string | null;
    /** Objectif métier (collecte puis sections guide) — aperçu carte */
    business_objective_preview?: string | null;
    /** True si la collecte apparaît parce que l’utilisateur est contributeur invité (pas propriétaire). */
    opened_as_contributor?: boolean;
    /** Jeton pour ouvrir la session contributeur /r/:token */
    contributor_invitation_token?: string | null;
    /** Nom du propriétaire (cartes home / bibliothèque quand opened_as_contributor) */
    owner_display_name?: string | null;
    /** ID du propriétaire principal (cartes home / bibliothèque) */
    owner_id?: string | null;
    /** Propriétaire principal de la collecte (API /collections/recent) */
    is_primary_owner?: boolean;
    /** Utilisateur courant est co-propriétaire de la collecte */
    is_collection_co_owner?: boolean;
    map_ids?: string[];
    linked_process_id?: string | null;
    has_responses?: boolean;
};

/** Owner, co-owner, or contributor may analyze collect data in Insights. */
export function canAnalyzeCollectionInInsights(col: RecentCollectCollectionSummary): boolean {
    if (col.is_primary_owner === true || col.is_collection_co_owner === true) return true;
    if (col.opened_as_contributor === true) return true;
    return (
        col.is_primary_owner === undefined
        && col.is_collection_co_owner === undefined
        && col.opened_as_contributor === undefined
    );
}

/** Propriétaire principal, co-propriétaire collecte, ou entrée sans flags (rétrocompat). */
export function canManageDataCollectionSummary(col: RecentCollectCollectionSummary): boolean {
    if (col.opened_as_contributor) return false;
    if (col.is_primary_owner === true || col.is_collection_co_owner === true) return true;
    return col.is_primary_owner === undefined && col.is_collection_co_owner === undefined;
}

/** Chemin d’ouverture depuis une ligne « recent » (owner/co-owner → workspace, contributeur → /r/:token). */
export function collectCollectionOpenPathFromSummary(row: RecentCollectCollectionSummary): string {
    const inviteTok = row.contributor_invitation_token?.trim();
    if (row.opened_as_contributor && inviteTok) {
        return `/r/${inviteTok}`;
    }
    return `/process/data-collect/${row.id}`;
}

/** Résout l’URL d’ouverture d’une collecte pour l’utilisateur courant (Insights, home, etc.). */
export async function resolveCollectCollectionOpenPath(
    collectionId: string,
    accessToken: string,
    currentUser?: { id?: string | null; email?: string | null },
): Promise<string | null> {
    if (!collectionId?.trim() || !accessToken) return null;

    const recentRes = await listRecentCollectCollections(accessToken, 100);
    if (recentRes.status === 200 && Array.isArray(recentRes.body?.data?.collections)) {
        const row = (recentRes.body.data.collections as RecentCollectCollectionSummary[]).find(
            (c) => c.id === collectionId,
        );
        if (row) {
            return collectCollectionOpenPathFromSummary(row);
        }
    }

    const detail = await getCollectDataCollection(collectionId, accessToken);
    if (detail.status !== 200 || !detail.body?.data) {
        return null;
    }

    const collection = detail.body.data.collection as {
        owner_id?: string;
        co_owner_ids?: string[];
    } | null;
    const contributors = (detail.body.data.contributors || []) as Array<{
        email?: string;
        invitation_token?: string;
    }>;

    const userId = String(currentUser?.id ?? '').trim();
    const userEmail = String(currentUser?.email ?? '').trim().toLowerCase();
    const ownerId = String(collection?.owner_id ?? '').trim();
    const coOwnerIds = (collection?.co_owner_ids ?? []).map((id) => String(id));

    if (userId && ownerId && userId === ownerId) {
        return `/process/data-collect/${collectionId}`;
    }
    if (userId && coOwnerIds.some((id) => id === userId)) {
        return `/process/data-collect/${collectionId}`;
    }

    if (userEmail) {
        const me = contributors.find(
            (c) => String(c.email ?? '').trim().toLowerCase() === userEmail,
        );
        const tok = me?.invitation_token?.trim();
        if (tok) {
            return `/r/${tok}`;
        }
    }

    return `/process/data-collect/${collectionId}`;
}

export async function listRecentCollectCollections(accessToken: string, limit = 25) {
    return FamlaServices('api_host', `/collections/recent?limit=${encodeURIComponent(String(limit))}`, 'GET', '', accessToken);
}

export type InsightsCollectionLinkRow = {
    id: string;
    name: string;
    map_ids?: string[];
    linked_process_id?: string | null;
    has_responses?: boolean;
    linked_map_ids?: string[];
};

export type InsightsInaccessibleCollectionLink = {
    id: string;
    name: string;
    reason: 'contributor' | 'no_access';
    linked_map_ids?: string[];
};

export type InsightsCollectionLinksResult = {
    manageable: InsightsCollectionLinkRow[];
    inaccessible: InsightsInaccessibleCollectionLink[];
};

/** Suppress inaccessible warnings when the user can access at least one linked collection. */
export function displayInaccessibleCollectionLinks(
    result: InsightsCollectionLinksResult,
): InsightsInaccessibleCollectionLink[] {
    if (result.manageable.length > 0) return [];
    return result.inaccessible;
}

/** Collections linked to selected maps for Insights scope (owner/co-owner vs contributor). */
export async function getInsightsCollectionLinks(
    mapIds: string[],
    accessToken: string,
): Promise<InsightsCollectionLinksResult> {
    const ids = [...new Set(mapIds.filter(Boolean))];
    if (!ids.length) {
        return { manageable: [], inaccessible: [] };
    }
    const qs = ids.map((id) => `map_ids=${encodeURIComponent(id)}`).join('&');
    const res = await FamlaServices('api_host', `/collections/insights-links?${qs}`, 'GET', '', accessToken);
    if (res.status === 200 && res.body?.data) {
        const data = res.body.data as InsightsCollectionLinksResult;
        return {
            manageable: Array.isArray(data.manageable) ? data.manageable : [],
            inaccessible: Array.isArray(data.inaccessible) ? data.inaccessible : [],
        };
    }
    return { manageable: [], inaccessible: [] };
}

export async function deleteCollectDataCollection(collectionId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}`, 'DELETE', {}, accessToken);
}

export async function withdrawMyCollectContribution(collectionId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/my-contribution`, 'DELETE', {}, accessToken);
}

/** Retrait via jeton /r/:token — sans session Famla. */
export async function withdrawCollectContributionByToken(token: string) {
    const res = await fetch(`${base()}/r/${encodeURIComponent(token)}/my-contribution`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
        credentials: 'omit',
    });
    let body: unknown = null;
    try {
        body = await res.json();
    } catch {
        body = null;
    }
    return { status: res.status, body };
}

/** Récents : retrait contributeur (session Famla ou repli jeton invitation). */
export async function withdrawRecentCollectContribution(
    row: RecentCollectCollectionSummary,
    accessToken?: string | null,
    options?: { fallbackToken?: string | null },
): Promise<{ ok: boolean; status: number }> {
    const isContributor = Boolean(row.opened_as_contributor) && !canManageDataCollectionSummary(row);
    if (!isContributor) return { ok: false, status: 0 };
    if (accessToken) {
        const res = await withdrawMyCollectContribution(row.id, accessToken);
        if (res.status === 200) return { ok: true, status: 200 };
    }
    const tok = row.contributor_invitation_token?.trim() || options?.fallbackToken?.trim();
    if (tok) {
        const res = await withdrawCollectContributionByToken(tok);
        if (res.status === 200) return { ok: true, status: res.status };
    }
    return { ok: false, status: 404 };
}

export async function patchCollectDataCollection(
    collectionId: string,
    payload: Record<string, unknown>,
    accessToken: string
) {
    return FamlaServices('api_host', `/collections/${collectionId}`, 'PATCH', payload, accessToken);
}

export async function addCollectDataCollectionCoOwner(collectionId: string, email: string, accessToken: string) {
    return FamlaServices(
        'api_host',
        `/collections/${collectionId}/add_co_owner`,
        'PUT',
        { mail: [email.trim().toLowerCase()] },
        accessToken
    );
}

export async function listOwnerSetupMessages(collectionId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/owner-messages`, 'GET', '', accessToken);
}

export async function postOwnerSetupMessage(collectionId: string, content: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/owner-messages`, 'POST', { content }, accessToken);
}

export type OwnerSetupPhase =
    | 'objective_what'
    | 'objective_clarify'
    | 'objective_why'
    | 'awaiting_context'
    | 'roles'
    | 'invites'
    | 'complete';

export type OwnerSetupTurnResult = {
    messages?: Array<{ id?: string; role?: string; content?: string; created_at?: string; createdAt?: string }>;
    assistant_message?: string;
    ready_for_guide?: boolean;
    setup_phase?: OwnerSetupPhase;
    message_id?: string;
    invite_role_binary_choice?: boolean;
    pending_invite_role?: string;
};

/** Owner setup — réponse JSON complète ; le client simule le stream localement. */
export async function postOwnerSetupTurn(
    collectionId: string,
    accessToken: string,
    body: { bootstrap?: boolean; content?: string },
) {
    return FamlaServices(
        'api_host',
        `/collections/${collectionId}/owner-setup/turn`,
        'POST',
        body,
        accessToken,
    ) as Promise<{
        status: number;
        body?: { data?: OwnerSetupTurnResult; description?: string };
    }>;
}

export async function patchOwnerSetupMessage(collectionId: string, messageId: string, content: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/owner-messages/${messageId}`, 'PATCH', { content }, accessToken);
}

export async function deleteOwnerSetupMessage(collectionId: string, messageId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/owner-messages/${messageId}`, 'DELETE', {}, accessToken);
}

export type OwnerSetupStreamHandlers = {
    onDelta: (text: string) => void;
    /** Server replaced streamed assistant text with canonical setup closing */
    onReplace?: (text: string) => void;
    onDone: (payload: {
        ready_for_guide?: boolean;
        setup_phase?: OwnerSetupPhase;
        message_id?: string;
        /** Aligné Process Map étape invitation : UI peut proposer Oui/Non pour l’intention d’inviter par rôle */
        invite_role_binary_choice?: boolean;
        /** Rôle à pré-remplir dans le modal « Ajouter un destinataire » après clic sur Oui */
        pending_invite_role?: string;
    }) => void;
    onError: (message: string) => void;
};

/** NDJSON stream: lines `{t:\"d\",c:\"...\"}` deltas, `{t:\"e\",...}` done, `{t:\"err\",m:\"...\"}` errors */
export async function streamOwnerSetup(
    collectionId: string,
    accessToken: string,
    body: { bootstrap?: boolean; content?: string },
    handlers: OwnerSetupStreamHandlers
): Promise<void> {
    const url = `${base()}/collections/${encodeURIComponent(collectionId)}/owner-setup/stream`;
    const resp = await fetch(url, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            Accept: 'application/x-ndjson',
        },
        body: JSON.stringify(body),
        credentials: 'include',
    });
    if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try {
            const j = await resp.json();
            msg = (j as { description?: string }).description || msg;
        } catch {
            /* ignore */
        }
        handlers.onError(msg);
        return;
    }
    if (!resp.body) {
        handlers.onError('No response body');
        return;
    }
    const reader = resp.body.getReader();
    const dec = new TextDecoder();
    let buf = '';
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let nl: number;
        while ((nl = buf.indexOf('\n')) >= 0) {
            const line = buf.slice(0, nl).trim();
            buf = buf.slice(nl + 1);
            if (!line) continue;
            try {
                const o = JSON.parse(line) as {
                    t: string;
                    c?: string;
                    m?: string;
                    ready_for_guide?: boolean;
                    setup_phase?: OwnerSetupPhase;
                    message_id?: string;
                    invite_role_binary_choice?: boolean;
                    pending_invite_role?: string;
                };
                if (o.t === 'd' && o.c) handlers.onDelta(o.c);
                else if (o.t === 'r' && o.c) handlers.onReplace?.(o.c);
                else if (o.t === 'e')
                    handlers.onDone({
                        ready_for_guide: o.ready_for_guide,
                        setup_phase: o.setup_phase,
                        message_id: o.message_id,
                        invite_role_binary_choice: o.invite_role_binary_choice,
                        pending_invite_role: o.pending_invite_role,
                    });
                else if (o.t === 'err') handlers.onError(o.m || 'Stream error');
            } catch {
                handlers.onError('Invalid stream line');
            }
        }
    }
    const tail = buf.trim();
    if (tail) {
        try {
            const o = JSON.parse(tail) as {
                t: string;
                ready_for_guide?: boolean;
                message_id?: string;
                invite_role_binary_choice?: boolean;
                pending_invite_role?: string;
            };
            if (o.t === 'e')
                handlers.onDone({
                    ready_for_guide: o.ready_for_guide,
                    message_id: o.message_id,
                    invite_role_binary_choice: o.invite_role_binary_choice,
                    pending_invite_role: o.pending_invite_role,
                });
        } catch {
            /* ignore */
        }
    }
}

export async function generateGuide(
    collectionId: string,
    accessToken: string,
    context_excerpt?: string
) {
    const body = context_excerpt != null ? { context_excerpt } : {};
    return FamlaServices('api_host', `/collections/${collectionId}/guide/generate`, 'POST', body, accessToken);
}

export async function getGuide(collectionId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/guide`, 'GET', '', accessToken);
}

export async function patchGuideSection(
    collectionId: string,
    sectionId: string,
    payload: { name?: string; objective?: string; order?: number },
    accessToken: string
) {
    return FamlaServices(
        'api_host',
        `/collections/${collectionId}/guide/sections/${sectionId}`,
        'PATCH',
        payload,
        accessToken
    );
}

export async function deleteGuideSection(collectionId: string, sectionId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/guide/sections/${sectionId}`, 'DELETE', {}, accessToken);
}

export async function addGuideSection(
    collectionId: string,
    roleSectionId: string,
    payload: { name: string; objective?: string },
    accessToken: string,
) {
    return FamlaServices(
        'api_host',
        `/collections/${collectionId}/guide/role-sections/${roleSectionId}/sections`,
        'POST',
        payload,
        accessToken,
    );
}

export async function addGuideQuestion(
    collectionId: string,
    sectionId: string,
    text: string,
    accessToken: string
) {
    return FamlaServices(
        'api_host',
        `/collections/${collectionId}/guide/sections/${sectionId}/questions`,
        'POST',
        { text },
        accessToken
    );
}

export async function patchGuideQuestion(
    collectionId: string,
    questionId: string,
    payload: { text?: string; order?: number; status?: string },
    accessToken: string
) {
    return FamlaServices(
        'api_host',
        `/collections/${collectionId}/guide/questions/${questionId}`,
        'PATCH',
        payload,
        accessToken
    );
}

export async function regenGuideQuestion(collectionId: string, questionId: string, accessToken: string) {
    return FamlaServices(
        'api_host',
        `/collections/${collectionId}/guide/questions/${questionId}/regen`,
        'POST',
        {},
        accessToken
    );
}

export async function approveGuide(collectionId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/guide/approve`, 'POST', {}, accessToken);
}

export async function addContributor(
    collectionId: string,
    body: { name: string; email: string; role?: string },
    accessToken: string
) {
    return FamlaServices('api_host', `/collections/${collectionId}/contributors`, 'POST', body, accessToken);
}

export async function deleteCollectContributor(collectionId: string, contributorId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/contributors/${contributorId}`, 'DELETE', {}, accessToken);
}

export async function remindCollectContributor(collectionId: string, contributorId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/contributors/${contributorId}/remind`, 'POST', {}, accessToken);
}

export async function sendCollection(collectionId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/send`, 'POST', {}, accessToken);
}

export async function getSynthesis(collectionId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/synthesis`, 'GET', '', accessToken);
}

/** Synthesis screen 6c — Table tab + headline stats (contributors × kept questions). */
export async function getCollectionResponseMatrix(collectionId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/response-matrix`, 'GET', '', accessToken);
}

/** Snapshot for collect analysis / insights preload — no dashboard generation. */
export async function getCollectAnalysisContext(collectionId: string, accessToken: string) {
    return FamlaServices('api_host', `/collections/${collectionId}/analysis-context`, 'GET', '', accessToken);
}

export type CollectDashboardPayload = {
    title: string;
    html: string;
    content_version: string;
    charts_used?: string[];
    generated_at?: string | null;
    credits_charged?: number;
};

export async function getCollectDashboard(
    collectionId: string,
    accessToken: string,
    theme?: 'light' | 'dark',
) {
    const q = theme ? `?theme=${theme}` : '';
    return FamlaServices('api_host', `/collections/${collectionId}/dashboard${q}`, 'GET', '', accessToken);
}

export async function generateCollectDashboard(
    collectionId: string,
    accessToken: string,
    body?: { user_instruction?: string; theme?: 'light' | 'dark' }
) {
    return FamlaServices(
        'api_host',
        `/collections/${collectionId}/dashboard/generate`,
        'POST',
        body ?? {},
        accessToken
    );
}

export async function getContributorThreadForOwner(
    collectionId: string,
    contributorId: string,
    accessToken: string
) {
    return FamlaServices(
        'api_host',
        `/collections/${collectionId}/contributors/${contributorId}/thread`,
        'GET',
        '',
        accessToken
    );
}

export type RecipientPreviewTurnPayload = {
    role: string;
    bootstrap?: boolean;
    user_message?: string;
    transcript?: Array<{ role: string; content: string }>;
    answered_question_ids?: string[];
};

/** Owner sandbox — adaptive contributor turn without persisting (preview page). */
export async function postRecipientPreviewTurn(
    collectionId: string,
    payload: RecipientPreviewTurnPayload,
    accessToken: string,
) {
    return FamlaServices(
        'api_host',
        `/collections/${collectionId}/recipient-preview/turn`,
        'POST',
        payload,
        accessToken,
    );
}

export function exportCollectionXlsxUrl(collectionId: string) {
    return `${base()}/collections/${collectionId}/export`;
}

export async function downloadCollectionExport(collectionId: string, accessToken: string) {
    const res = await fetch(exportCollectionXlsxUrl(collectionId), {
        method: 'GET',
        headers: { Accept: '*/*', Authorization: `Bearer ${accessToken}` },
        credentials: 'include',
    });
    if (!res.ok) return { ok: false as const, status: res.status };
    const blob = await res.blob();
    return { ok: true as const, blob };
}

/** Public contributor session — no auth cookies required beyond fetch CORS */
export async function getCollectSharedResponderEntry(collectionToken: string) {
    const res = await fetch(`${base()}/r/c/${encodeURIComponent(collectionToken)}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'omit',
    });
    return { status: res.status, body: await res.json() };
}

export async function joinCollectViaSharedResponderLink(collectionToken: string, role?: string) {
    const res = await fetch(`${base()}/r/c/${encodeURIComponent(collectionToken)}/join`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ role: role ?? '' }),
    });
    return { status: res.status, body: await res.json() };
}

export async function getCollectContributorSession(token: string) {
    const res = await fetch(`${base()}/r/${token}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'omit',
    });
    return { status: res.status, body: await res.json() };
}

export async function postCollectContributorMessage(token: string, content: string) {
    const res = await fetch(`${base()}/r/${token}/messages`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify({ content }),
    });
    return { status: res.status, body: await res.json() };
}

export async function postCollectContributorInvite(
    token: string,
    payload: { name: string; email: string; role: string }
) {
    const res = await fetch(`${base()}/r/${token}/invite`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify(payload),
    });
    return { status: res.status, body: await res.json() };
}

export function exportCollectContributorExportUrl(token: string) {
    return `${base()}/r/${token}/export`;
}

export async function downloadContributorTranscript(token: string) {
    const res = await fetch(exportCollectContributorExportUrl(token), {
        method: 'GET',
        headers: { Accept: '*/*' },
        credentials: 'omit',
    });
    if (!res.ok) return { ok: false as const, status: res.status };
    const blob = await res.blob();
    return { ok: true as const, blob };
}
